import { cn } from "./classNames-DkX30VJU.js";
import { Tooltip } from "./utils-DGd7lPd6.js";
import { AlertTriangle, ChevronDown, CircleCheck, Info } from "./icons-DDR9dFSr.js";
import { Collapsible } from "./Collapsible-zsXk4Vpp.js";
import { useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { jsx, jsxs } from "react/jsx-runtime";
// src/components/Alert/AlertAction.tsx
const AlertAction = ({ ref, className, asChild = false, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "div", {
		...props,
		ref,
		className: cn("tw:col-start-3", "tw:row-start-1", "tw:self-start", "tw:flex", "tw:gap-2", className)
	});
};
// src/components/Alert/AlertCollapsibleIcon.tsx
const AlertCollapsibleIcon = ({ open }) => /* @__PURE__ */ jsx(Tooltip, {
	placement: "top",
	tooltip: open ? "Collapse alert" : "Expand alert",
	triggerAsChild: true,
	children: /* @__PURE__ */ jsx(ChevronDown, { className: cn("tw:mt-px", "tw:size-5", "tw:text-muted-foreground", "tw:group-data-[state=open]/alert:rotate-180", "tw:group-data-[variant=info]/alert:group-hover:text-info", "tw:group-data-[variant=warning]/alert:group-hover:text-warning", "tw:group-data-[variant=error]/alert:group-hover:text-destructive", "tw:group-data-[variant=success]/alert:group-hover:text-success") })
});
// src/components/Alert/AlertDescription.tsx
const AlertDescription = ({ ref, children, className, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	ref,
	className: cn("tw:col-start-2", "tw:group-has-[[data-slot=alert-title]]/alert:col-end-[-1]", "tw:group-has-[[data-slot=alert-title]]/alert:before:block", "tw:group-has-[[data-slot=alert-title]]/alert:before:h-1", "tw:group-has-[[data-slot=alert-title]]/alert:before:content-['']", "tw:wrap-anywhere", "tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4", "tw:[&_svg]:shrink-0", className),
	children
});
// src/components/Alert/AlertIcon.tsx
const VARIANT_ICONS = {
	info: /* @__PURE__ */ jsx(Info, {}),
	warning: /* @__PURE__ */ jsx(AlertTriangle, {}),
	error: /* @__PURE__ */ jsx(AlertTriangle, {}),
	success: /* @__PURE__ */ jsx(CircleCheck, {})
};
const AlertIcon = ({ variant, icon, className, ...rest }) => /* @__PURE__ */ jsx("span", {
	...rest,
	"data-slot": "alert-icon",
	className: cn("tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:self-start", "tw:p-0.5", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", "tw:group-data-[variant=info]/alert:text-info", "tw:group-data-[variant=warning]/alert:text-warning", "tw:group-data-[variant=error]/alert:text-destructive", "tw:group-data-[variant=success]/alert:text-success", className),
	children: icon ?? VARIANT_ICONS[variant]
});
// src/components/Alert/AlertRoot.tsx
const AlertRoot = ({ ref, children, className, variant, ...rest }) => /* @__PURE__ */ jsx("div", {
	...rest,
	ref,
	"data-variant": variant,
	className: cn("tw:group/alert", "tw:grid", "tw:grid-cols-[0_1fr_auto]", "tw:has-[[data-slot=alert-icon]]:grid-cols-[auto_1fr_auto]", "tw:has-[[data-slot=alert-icon]]:gap-x-2", "tw:text-sm", "tw:text-foreground", "tw:text-left", "tw:p-4", "tw:rounded-[var(--radius-lg,0.25rem)]", "tw:border", "tw:border-solid", "tw:data-[variant=info]:bg-info/10", "tw:data-[variant=info]:border-info/25", "tw:data-[variant=warning]:bg-warning/10", "tw:data-[variant=warning]:border-warning/25", "tw:data-[variant=error]:bg-destructive/10", "tw:data-[variant=error]:border-destructive/25", "tw:data-[variant=success]:bg-success/10", "tw:data-[variant=success]:border-success/25", className),
	children
});
// src/components/Alert/AlertTitle.tsx
const AlertTitle = ({ ref, className, ...props }) => /* @__PURE__ */ jsx("div", {
	...props,
	ref,
	"data-slot": "alert-title",
	className: cn("tw:group/alert-title", "tw:flex", "tw:col-start-2", "tw:font-medium", "tw:items-center", className)
});
// src/components/Alert/Alert.tsx
/**
* Persistent inline banner for contextual messages tied to the page layout.
*
* **When to use:** in-page messages; `mode="collapsible"` hides long error details.
* **When NOT to use:** transient feedback (`toast()`), field errors (`FormGroup state="error"`),
* confirmations (`Popconfirm`).
*
* @see RECIPES.md#alert-with-action
*/
const Alert = ({ title, variant = "info", children, hideIcon = false, icon, className, actions, mode = "default", defaultOpen = true, open: openProp, onOpenChange: onOpenChangeProp, ...rest }) => {
	const isControlled = openProp !== void 0;
	const [innerOpen, setInnerOpen] = useState(defaultOpen);
	const open = isControlled ? openProp : innerOpen;
	const handleOpenChange = (nextOpen) => {
		if (!isControlled) setInnerOpen(nextOpen);
		onOpenChangeProp?.(nextOpen);
	};
	if (mode === "collapsible" && !!title && !!children && !actions) return /* @__PURE__ */ jsx(Collapsible, {
		open,
		onOpenChange: handleOpenChange,
		asChild: true,
		children: /* @__PURE__ */ jsxs(AlertRoot, {
			...rest,
			variant,
			className,
			children: [/* @__PURE__ */ jsxs(Collapsible.Trigger, {
				className: cn("tailwindcss-preflight", "tw:group", "tw:col-span-full", "tw:grid", "tw:grid-cols-subgrid", "tw:rounded-sm", "tw:focus-visible:outline", "tw:focus-visible:outline-2", "tw:focus-visible:outline-ring"),
				children: [
					!hideIcon && /* @__PURE__ */ jsx(AlertIcon, {
						variant,
						icon
					}),
					/* @__PURE__ */ jsx(AlertTitle, { children: title }),
					/* @__PURE__ */ jsx(AlertCollapsibleIcon, { open })
				]
			}), /* @__PURE__ */ jsx(Collapsible.Content, {
				asChild: true,
				children: /* @__PURE__ */ jsx(AlertDescription, { children })
			})]
		})
	});
	return /* @__PURE__ */ jsxs(AlertRoot, {
		...rest,
		variant,
		className,
		children: [
			!hideIcon && /* @__PURE__ */ jsx(AlertIcon, {
				variant,
				icon
			}),
			title && /* @__PURE__ */ jsx(AlertTitle, { children: title }),
			children && /* @__PURE__ */ jsx(AlertDescription, { children }),
			actions && /* @__PURE__ */ jsx(AlertAction, { children: actions })
		]
	});
};
export { Alert };

//# sourceMappingURL=Alert-Cnx8ZiEr.js.map